if (!customElements.get('size-range-toggle')) {
  class SizeRangeToggle extends HTMLElement {
    connectedCallback() {
      this.picker = this.closest('variant-picker');
      this.buttons = this.querySelectorAll('.size-range-toggle__btn');
      this.currentRange = this.picker.dataset.sizeRange || 'standard';
      this.plusUrl = this.picker.dataset.plusUrl || null;
      this.standardUrl = this.picker.dataset.standardUrl || this.picker.dataset.url;

      if (!this.plusUrl && !this.picker.dataset.standardUrl) return;

      this._pageCache = {};
      this._cacheCurrentPage();

      this.buttons.forEach(btn =>
        btn.addEventListener('click', () => this.handleToggle(btn))
      );
    }

    _cacheCurrentPage() {
      const url = this.picker.dataset.url;
      this._pageCache[url] = {
        slides: [...document.querySelectorAll('media-gallery .main-image .slider__item')]
          .map(el => el.outerHTML),
        collageItems: [...document.querySelectorAll('media-gallery .product-media-collage__item')]
          .map(el => el.outerHTML),
        thumbItems: [...document.querySelectorAll('media-gallery .thumbnails .slider__item')]
          .map(el => el.outerHTML),
        mediaGroupScript: document.querySelector('.js-data-variant-media')?.textContent,
        variantJson: this.picker.querySelector('[type="application/json"]')?.textContent,
        colorFieldset: this.picker.querySelector('.option-selector[data-option="Color"]')?.outerHTML,
        sizeFieldset: this.picker.querySelector('.option-selector[data-option="Size"]')?.outerHTML,
      };
    }

    handleToggle(btn) {
      const range = btn.dataset.range;
      if (range === this.currentRange) return;

      const colorInput = this.picker.querySelector('.option-selector[data-option="Color"] input.js-option:checked');
      const currentColor = colorInput?.dataset.originalValue || colorInput?.value || null;

      const targetUrl = range === 'extended' ? this.plusUrl : this.standardUrl;
      if (!targetUrl) return;

      this._swapProduct(targetUrl, currentColor, btn, range);
    }

    _swapProduct(productUrl, matchColor, activeBtn, range) {
      this.buttons.forEach(b => { b.classList.remove('is-active'); b.disabled = true; });
      activeBtn.classList.add('is-loading');

      if (this._pageCache[productUrl]) {
        this._applyFromCache(this._pageCache[productUrl], productUrl, matchColor, activeBtn, range);
        return;
      }

      fetch(productUrl)
        .then(r => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.text();
        })
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const cached = {
            slides: [...doc.querySelectorAll('media-gallery .main-image .slider__item')]
              .map(el => el.outerHTML),
            collageItems: [...doc.querySelectorAll('media-gallery .product-media-collage__item')]
              .map(el => el.outerHTML),
            thumbItems: [...doc.querySelectorAll('media-gallery .thumbnails .slider__item')]
              .map(el => el.outerHTML),
            mediaGroupScript: doc.querySelector('.js-data-variant-media')?.textContent,
            variantJson: doc.querySelector('variant-picker [type="application/json"]')?.textContent,
            colorFieldset: doc.querySelector('.option-selector[data-option="Color"]')?.outerHTML,
            sizeFieldset: doc.querySelector('.option-selector[data-option="Size"]')?.outerHTML,
          };
          this._pageCache[productUrl] = cached;
          this._applyFromCache(cached, productUrl, matchColor, activeBtn, range);
        })
        .catch(err => {
          console.error('[SizeRangeToggle] fetch failed', err);
          this.buttons.forEach(b => {
            b.disabled = false;
            b.classList.remove('is-loading');
            if (b.dataset.range === this.currentRange) b.classList.add('is-active');
          });
        });
    }

    _applyFromCache(cached, productUrl, matchColor, activeBtn, range) {
      const picker = this.picker;
      const mediaGallery = document.querySelector('media-gallery');

      // 1. Update URLs and range on both the JS instance and picker dataset
      if (range === 'extended') {
        this.standardUrl = picker.dataset.url;
        picker.dataset.standardUrl = picker.dataset.url;
        picker.dataset.sizeRange = 'extended';
        delete picker.dataset.plusUrl;
      } else {
        this.plusUrl = picker.dataset.url;
        picker.dataset.plusUrl = picker.dataset.url;
        picker.dataset.sizeRange = 'standard';
        delete picker.dataset.standardUrl;
      }
      this.currentRange = range;
      picker.dataset.url = productUrl;

      // 2. Swap gallery slides using Symmetry's exact selector pattern
      const mainCarousel = mediaGallery?.querySelector('.main-image carousel-slider .slider__grid');
      if (mainCarousel && cached.slides.length) {
        mainCarousel.innerHTML = cached.slides.join('');
      }

      const collage = mediaGallery?.querySelector('.product-media-collage');
      if (collage && cached.collageItems.length) {
        collage.innerHTML = cached.collageItems.join('');
      }

      const thumbGrid = mediaGallery?.querySelector('.thumbnails .slider__grid');
      if (thumbGrid && cached.thumbItems.length) {
        thumbGrid.innerHTML = cached.thumbItems.join('');
      }

      // 3. Replace js-data-variant-media BEFORE resetting mediaGroupingEnabled
      // so getMediaGroupData re-reads the correct script on next call
      const mediaScript = document.querySelector('.js-data-variant-media');
      if (mediaScript && cached.mediaGroupScript) {
        mediaScript.textContent = cached.mediaGroupScript;
      }

      // 4. Reset media-gallery internal state — delete variantMediaData cache first
      // so getMediaGroupData re-reads the updated script tag instead of returning stale data
      if (mediaGallery) {
        delete mediaGallery.variantMediaData;
        mediaGallery.mediaGroupingEnabled = mediaGallery.getMediaGroupData();
        mediaGallery.currentMediaGroup = null;
        mediaGallery.mediaLists = this._rebuildMediaLists(mediaGallery);
      }

      // 5. Set active media group immediately for the target color
      if (mediaGallery && cached.mediaGroupScript) {
        const normalize = str => str.toLowerCase().replace(/\s+/g, '').replace(/ll+/g, 'l');
        let targetGroup = null;

        try {
          const mediaData = JSON.parse(cached.mediaGroupScript);
          const groups = [...new Set(Object.values(mediaData.media || {}).map(v => v.group))];
          targetGroup = groups.find(g => normalize(g) === normalize(matchColor || '')) || groups[0];
        } catch(e) {}

        if (targetGroup) {
          mediaGallery.setActiveMediaGroup(targetGroup);

          // Mirror visibility to prefixed IDs (e.g. "template--...main-12345")
          // that setActiveMediaGroup skips because they're not in the JSON
          try {
            const mediaData = JSON.parse(cached.mediaGroupScript);
            mediaGallery.mediaLists.forEach(list => {
              list.forEach(el => {
                const rawId = el.dataset.mediaId;
                if (!mediaData.media[rawId]) {
                  const numericId = rawId.replace(/^.*-(\d+)$/, '$1');
                  const matched = mediaData.media[numericId];
                  if (matched) {
                    el.hidden = matched.group !== targetGroup;
                  }
                }
              });
            });
          } catch(e) {}
        }
      }

      // 6. Swap variant JSON blob FIRST so picker.data is correct before fieldsets render
      const jsonEl = picker.querySelector('[type="application/json"]');
      if (jsonEl && cached.variantJson) {
        jsonEl.textContent = cached.variantJson;
      }

      // 7. Re-init picker data immediately after JSON swap so updateAvailability
      // uses the correct variant list when fieldset changes trigger it
      picker.data = picker.getVariantData();

      // 8. Swap color fieldset
      const currentColorFieldset = picker.querySelector('.option-selector[data-option="Color"]');
      if (currentColorFieldset && cached.colorFieldset) {
        const tmp = document.createElement('div');
        tmp.innerHTML = cached.colorFieldset;
        currentColorFieldset.replaceWith(tmp.firstElementChild);
      }

      // 9. Swap size fieldset
      const currentSizeFieldset = picker.querySelector('.option-selector[data-option="Size"]');
      if (currentSizeFieldset && cached.sizeFieldset) {
        const tmp = document.createElement('div');
        tmp.innerHTML = cached.sizeFieldset;
        currentSizeFieldset.replaceWith(tmp.firstElementChild);
      }

      // 10. Re-init remaining picker internals
      picker.optionSelectors = picker.querySelectorAll('.option-selector:not(.option-selector--custom)');
      picker.selectedOptions = picker.getSelectedOptions();
      picker.variant = picker.getSelectedVariant();

      // 11. Strip data-product-url from all inputs so Symmetry's handleVariantChange
      // doesn't bail on pathname mismatch
      picker.querySelectorAll('input.js-option[data-product-url]').forEach(input => {
        input.removeAttribute('data-product-url');
      });

      // 12. Re-bind Symmetry's change listener on the picker
      if (picker._boundHandleVariantChange) {
        picker.removeEventListener('change', picker._boundHandleVariantChange);
      }
      picker._boundHandleVariantChange = picker.handleVariantChange.bind(picker);
      picker.addEventListener('change', picker._boundHandleVariantChange);
      if (!picker.hasAttribute('loaded')) {
        picker.setAttribute('loaded', '');
      }

      // 13. Re-bind media-gallery's on:variant:change listener on the section.
      // The wrapper nulls currentMediaGroup before each call so setActiveMediaGroup
      // never bails on the "already same group" guard.
      // Also keeps Kaching widget in sync with the selected variant.
      const section = picker.closest('.js-product');
      if (mediaGallery && section) {
        if (mediaGallery._boundOnVariantChange) {
          section.removeEventListener('on:variant:change', mediaGallery._boundOnVariantChange);
        }
        mediaGallery._boundOnVariantChange = function(evt) {
          mediaGallery.currentMediaGroup = null;
          mediaGallery.onVariantChange(evt);
          // Keep Kaching widget in sync with selected variant
          const kachingWidget = document.querySelector('kaching-bundles-block');
          if (kachingWidget && kachingWidget.style.display !== 'none') {
            kachingWidget.currentVariantId = evt.detail?.variant?.id;
          }
        };
        section.addEventListener('on:variant:change', mediaGallery._boundOnVariantChange);
      }

      // 14. Select default size silently, then select color to fire variant change
      requestAnimationFrame(() => {
        this._selectDefaultSize(picker);
        requestAnimationFrame(() => {
          if (mediaGallery) mediaGallery.currentMediaGroup = null;
          this._selectColor(picker, matchColor);

          // 15. Show/hide Kaching bundle widget — extended product has no bundle
          const kachingWidget = document.querySelector('kaching-bundles-block');
          console.log('[toggle] kaching hide step — range:', range, '| widget found:', !!kachingWidget);
          if (kachingWidget) {
            if (range === 'extended') {
              kachingWidget.style.display = 'none';
              console.log('[toggle] kaching hidden, display now:', kachingWidget.style.display);
            } else {
              kachingWidget.style.display = '';
              const newVariant = picker.getSelectedVariant();
              if (newVariant) kachingWidget.currentVariantId = newVariant.id;
              console.log('[toggle] kaching shown, variant synced:', newVariant?.id);
            }
          }

          this.buttons.forEach(b => {
            b.disabled = false;
            b.classList.remove('is-loading', 'is-active');
            if (b === activeBtn) b.classList.add('is-active');
          });
        });
      });
    }

    _rebuildMediaLists(mediaGallery) {
      // Match Symmetry's exact constructor selectors
      const lists = [
        mediaGallery.querySelectorAll('.main-image .slider__item'),
        mediaGallery.querySelectorAll('.product-media-collage__item'),
        mediaGallery.querySelectorAll('.thumbnails .slider__item'),
      ];
      const galleryViewer = mediaGallery.querySelector('.gallery-viewer');
      if (galleryViewer) {
        lists.push(galleryViewer.querySelectorAll('.gallery-viewer__thumb'));
      }
      return lists;
    }

    _selectDefaultSize(picker) {
      const sizeSelector = picker.querySelector('.option-selector[data-option="Size"]');
      if (!sizeSelector) return;
      const firstAvailable = sizeSelector.querySelector('input.js-option:not(.is-unavailable):not(.is-nonexistent)')
        || sizeSelector.querySelector('input.js-option');
      if (firstAvailable) firstAvailable.checked = true;
    }

    _selectColor(picker, matchColor) {
      const normalize = str => str.toLowerCase().replace(/\s+/g, '').replace(/ll+/g, 'l');
      const colorSelector = picker.querySelector('.option-selector[data-option="Color"]');

      let target = null;

      if (matchColor && colorSelector) {
        target = Array.from(colorSelector.querySelectorAll('input.js-option'))
          .find(i =>
            i.style.display !== 'none' && (
              normalize(i.value) === normalize(matchColor) ||
              normalize(i.dataset.originalValue || '') === normalize(matchColor)
            )
          );
      }

      if (!target) {
        target = colorSelector
          ? colorSelector.querySelector('input.js-option:not(.is-nonexistent):not([style*="display: none"])')
          : picker.querySelector('input.js-option');
      }

      if (!target) return;

      target.checked = true;

      // Re-read with fresh DOM state
      picker.optionSelectors = picker.querySelectorAll('.option-selector:not(.option-selector--custom)');
      picker.selectedOptions = picker.getSelectedOptions();

      const variant = picker.getSelectedVariant();

      if (!variant) {
        const sizeSelector = picker.querySelector('.option-selector[data-option="Size"]');
        const sizes = sizeSelector?.querySelectorAll('input.js-option') || [];
        for (const sizeInput of sizes) {
          sizeInput.checked = true;
          picker.selectedOptions = picker.getSelectedOptions();
          if (picker.getSelectedVariant()) break;
          sizeInput.checked = false;
        }
        picker.selectedOptions = picker.getSelectedOptions();
      }

      if (!picker.getSelectedVariant()) {
        console.warn('[SizeRangeToggle] no matching variant found, skipping change event');
        return;
      }

      // Dispatch with selectedOptions in detail so Symmetry's onVariantChange
      // reads the correct color group via evt.detail.selectedOptions
      target.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        detail: {
          selectedOptions: picker.selectedOptions,
          variant: picker.getSelectedVariant()
        }
      }));
    }
  }

  customElements.define('size-range-toggle', SizeRangeToggle);
}